import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import LocationIcon from '../components/common/LocationIcon';
import { ProjectsGridFiltered } from '../components/home/ProjectsSection';
import HeroSection from '../components/home/HeroSection';
import AboutSection from '../components/home/AboutSection';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  return (
    <div>
      <HeroSection />

      {/* About Section */}
      <AboutSection />

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title services-title-home">
              Our <span className="highlight">Services</span>
            </h2>
            <p className="section-subtitle">
              🏗️ Construction Excellence | 🏢 Property Development | 📋 Real Estate Consultancy<br />
              "Building Dreams, Creating Value, Ensuring Satisfaction"
            </p>
          </motion.div>

          <div className="horizontal-services-row">
            <motion.div
              className="service-card"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="service-card-header">
                <div className="service-icon">🏗️</div>
                <h3 className="service-card-title">CONSTRUCTION SERVICES</h3>
              </div>
              <div className="service-card-content">
                <ul className="service-list">
                  <li>Residential Construction: Luxury villas, apartments, and custom homes</li>
                  <li>Commercial Construction: Office complexes, retail spaces, and mixed-use developments</li>
                  <li>Industrial Construction: Warehouses, manufacturing facilities, and logistics centers</li>
                  <li>Renovation & Retrofitting: Modernization of existing structures</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              className="service-card"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="service-card-header">
                <div className="service-icon">🏢</div>
                <h3 className="service-card-title">PROPERTY DEVELOPMENT</h3>
              </div>
              <div className="service-card-content">
                <ul className="service-list">
                  <li>Land Acquisition & Development: Strategic site selection and development planning</li>
                  <li>Project Conceptualization: From feasibility studies to detailed project planning</li>
                  <li>Regulatory Compliance: Government approvals and clearances management</li>
                  <li>Financial Structuring: Investment planning and funding arrangements</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              className="service-card"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="service-card-header">
                <div className="service-icon">📋</div>
                <h3 className="service-card-title">CONSULTANCY & ADVISORY</h3>
              </div>
              <div className="service-card-content">
                <ul className="service-list">
                  <li>Real Estate Advisory: Market analysis, investment guidance, and portfolio management</li>
                  <li>Project Management: End-to-end project coordination and execution oversight</li>
                  <li>Regulatory Consulting: RERA, environmental, and municipal approvals</li>
                  <li>Due Diligence: Property evaluation and risk assessment</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              className="service-card"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="service-card-header">
                <div className="service-icon">🛒</div>
                <h3 className="service-card-title">CONSTRUCTION MATERIALS & TRADING</h3>
              </div>
              <div className="service-card-content">
                <ul className="service-list">
                  <li>Quality Materials Sourcing: Partnerships with leading suppliers across India</li>
                  <li>Supply Chain Management: Efficient procurement and logistics solutions</li>
                  <li>Quality Assurance: Rigorous testing and certification processes</li>
                  <li>Cost Optimization: Value engineering and competitive pricing</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              className="service-card"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="service-card-header">
                <div className="service-icon">🤝</div>
                <h3 className="service-card-title">REAL ESTATE SERVICES</h3>
              </div>
              <div className="service-card-content">
                <ul className="service-list">
                  <li>Buying & Selling: Comprehensive transaction management</li>
                  <li>Leasing Services: Commercial and residential lease facilitation</li>
                  <li>Property Management: Maintenance and operational support</li>
                  <li>Investment Services: Portfolio development and management</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Showcase Section with navigation on click */}
      <ProjectsGridFiltered onProjectClick={project => navigate('/projects', { state: { selectedProject: project } })} />

      {/* Experience & Portfolio Section */}
      <section className="experience-section">
        {/* Animated Background Elements */}
        <div className="experience-bg-pattern"></div>
        <div className="experience-bg-glow"></div>
        
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title experience-main-title">Experience & Portfolio</h2>
            <p className="section-subtitle experience-subtitle-text">
              Track record of excellence and proven success across South India
            </p>
          </motion.div>

          {/* Statistics Banner */}
          <motion.div
            className="experience-stats-banner"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="stat-item">
              <div className="stat-icon">💰</div>
              <div className="stat-value">₹5,500+</div>
              <div className="stat-label">Crores in Transactions</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">⏱️</div>
              <div className="stat-value">20+</div>
              <div className="stat-label">Years Experience</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">🌆</div>
              <div className="stat-value">10+</div>
              <div className="stat-label">Cities Served</div>
            </div>
          </motion.div>

          <div className="portfolio-grid">
            {/* Track Record Card */}
            <motion.div
              className="portfolio-card track-record-card"
              initial={{ opacity: 0, y: 50, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                rotateY: 2,
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
            >
              <div className="card-glow-effect"></div>
              <div className="portfolio-card-header">
                <div className="portfolio-icon-wrapper">
                  <div className="portfolio-icon">🏆</div>
                  <div className="icon-ring"></div>
                </div>
                <h3 className="portfolio-card-title">Track Record of Excellence</h3>
                <div className="card-accent-line"></div>
              </div>
              <div className="portfolio-card-content">
                <div className="amount-highlight">
                  <span className="amount-value">₹5,500+</span>
                  <span className="amount-unit">Crores</span>
                </div>
                <p className="portfolio-description">
                  Successfully managed transactions across:
                </p>
                <div className="cities-list">
                  <span className="city-tag">Chennai</span>
                  <span className="city-tag">Coimbatore</span>
                  <span className="city-tag">Coonoor</span>
                  <span className="city-tag">Kodaikanal</span>
                  <span className="city-tag">Madurai</span>
                  <span className="city-tag">Thiruvarur</span>
                  <span className="city-tag">Trichy</span>
                  <span className="city-tag">Pondicherry</span>
                  <span className="city-tag">Bangalore</span>
                  <span className="city-tag">Cochin</span>
                  <span className="city-tag">Calicut</span>
                </div>
              </div>
            </motion.div>

            {/* Current Operations Card */}
            <motion.div
              className="portfolio-card operations-card"
              initial={{ opacity: 0, y: 50, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                rotateY: 2,
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
            >
              <div className="card-glow-effect"></div>
              <div className="portfolio-card-header">
                <div className="portfolio-icon-wrapper">
                  <div className="portfolio-icon">🏭</div>
                  <div className="icon-ring"></div>
                </div>
                <h3 className="portfolio-card-title">Current Operations</h3>
                <div className="card-accent-line"></div>
              </div>
              <div className="portfolio-card-content">
                <div className="operations-highlight">
                  <div className="operation-stat">
                    <span className="operation-value">12M</span>
                    <span className="operation-unit">Sq. Ft</span>
                    <span className="operation-label">Operational</span>
                  </div>
                  <div className="operation-plus">+</div>
                  <div className="operation-stat">
                    <span className="operation-value">4M</span>
                    <span className="operation-unit">Sq. Ft</span>
                    <span className="operation-label">Upcoming</span>
                  </div>
                </div>
                <p className="portfolio-description operations-description">
                  <strong>NDR Warehousing Consultancy</strong><br/>
                  Managing portfolio across South Zone
                </p>
                <div className="operations-badge">
                  <span>Active Operations</span>
                </div>
              </div>
            </motion.div>

            {/* Client Portfolio Card */}
            <motion.div
              className="portfolio-card clients-card"
              initial={{ opacity: 0, y: 50, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                rotateY: 2,
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
            >
              <div className="card-glow-effect"></div>
              <div className="portfolio-card-header">
                <div className="portfolio-icon-wrapper">
                  <div className="portfolio-icon">🤝</div>
                  <div className="icon-ring"></div>
                </div>
                <h3 className="portfolio-card-title">Prestigious Client Portfolio</h3>
                <div className="card-accent-line"></div>
              </div>
              <div className="portfolio-card-content">
                <div className="clients-content">
                  <div className="client-section">
                    <h4 className="client-section-title">
                      <span className="title-icon">🏢</span>
                      Corporate Clients
                    </h4>
                    <div className="client-logos-grid">
                      <span className="client-logo">Tata</span>
                      <span className="client-logo">DLF</span>
                      <span className="client-logo">Cognizant</span>
                      <span className="client-logo">ICICI</span>
                      <span className="client-logo">HP</span>
                      <span className="client-logo">Siemens</span>
                      <span className="client-logo">HSBC</span>
                      <span className="client-logo">Airtel</span>
                      <span className="client-logo">+12 More</span>
                    </div>
                  </div>
                  <div className="client-section">
                    <h4 className="client-section-title">
                      <span className="title-icon">🤝</span>
                      Industry Partnerships
                    </h4>
                    <div className="partnership-tags">
                      <span className="partnership-tag">Architects</span>
                      <span className="partnership-tag">Financial Institutions</span>
                      <span className="partnership-tag">Government Bodies</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Projects Overview Card */}
            <motion.div
              className="portfolio-card projects-overview-card"
              initial={{ opacity: 0, y: 50, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                rotateY: 2,
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
            >
              <div className="card-glow-effect"></div>
              <div className="portfolio-card-header">
                <div className="portfolio-icon-wrapper">
                  <div className="portfolio-icon">🏗️</div>
                  <div className="icon-ring"></div>
                </div>
                <h3 className="portfolio-card-title">Completed Projects</h3>
                <div className="card-accent-line"></div>
              </div>
              <div className="portfolio-card-content">
                <div className="projects-summary">
                  <div className="project-count">
                    <span className="count-number">7+</span>
                    <span className="count-label">Major Projects</span>
                  </div>
                  <div className="project-types">
                    <div className="project-type residential-type">
                      <div className="project-type-icon">🏠</div>
                      <h5>Residential</h5>
                      <p>Vasuthalayam, Guru Guha Residences, Lalith Residence, Bharadwaj Apartments</p>
                    </div>
                    <div className="project-type commercial-type">
                      <div className="project-type-icon">🏢</div>
                      <h5>Commercial</h5>
                      <p>Sriram Nivas, Niramaya Apartments, Aashiritha</p>
                    </div>
                  </div>
                </div>
                <Link 
                  to="/projects" 
                  className="view-projects-btn"
                  onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}
                >
                  <span>View All Projects</span>
                  <span className="btn-arrow">→</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section id="leadership" className="leadership-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Leadership</h2>
            <p className="section-subtitle">
              Meet our visionary leader driving excellence in construction and real estate
            </p>
          </motion.div>

          <motion.div
            className="leadership-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="leader-card">
              <div className="leader-image">
                <img
                  src="/images/leadership.jpg"
                  alt="Sundar Rajan Jagannathan"
                  className="leader-avatar"
                />
              </div>
              <div className="leader-info">
                <h3 className="leader-name">Sundar Rajan Jagannathan (SRJ)</h3>
                <p className="leader-title">Founder & Managing Partner</p>
                <div className="leader-credentials">
                  <h4>Professional Credentials:</h4>
                  <ul>
                    <li>MBA with specialization in Property Market Analysis & Forecasting</li>
                    <li>20+ Years of leadership experience in real estate and construction</li>
                    <li>Author of "Are You OK Baby!" concept</li>
                    <li>Board Chairman of Vibha Listens India Private Limited</li>
                  </ul>
                  <h4>Career Highlights:</h4>
                  <ul>
                    <li>Radiance Realty Developers (2010-2013, 2015-2021): COO - Diversified Business Group</li>
                    <li>CB Richard Ellis (CBRE) (2001-2015): Research Trainee to Head of Industrial & Investment Vertical</li>
                    <li>NDR Warehousing (Current): South Zone Operations Consultant</li>
                  </ul>
                  <h4>Expertise Areas:</h4>
                  <ul>
                    <li>Land Acquisition & Development</li>
                    <li>Project Conceptualization & Planning</li>
                    <li>Government Relations & Approvals</li>
                    <li>Financial Structuring & Closures</li>
                    <li>Sales & Marketing Strategy</li>
                    <li>Client Relationship Management</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Vibha Section */}
      <section className="why-choose-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Why Choose Vibha Corporation</h2>
            <p className="section-subtitle">
              Proven excellence and unwavering commitment to quality
            </p>
          </motion.div>

          <div className="horizontal-features-row">
            {[
              {
                icon: '🎯',
                title: 'Proven Expertise',
                description: 'Two decades of hands-on experience in every aspect of construction and real estate'
              },
              {
                icon: '💰',
                title: 'Financial Strength',
                description: 'Track record of managing ₹5,500+ Crores in transactions with zero defaults'
              },
              {
                icon: '🏛️',
                title: 'Regulatory Excellence',
                description: 'Deep understanding of Indian construction laws, RERA compliance, and government procedures'
              },
              {
                icon: '🤝',
                title: 'Client-Centric Approach',
                description: 'Commitment to customer satisfaction with transparent communication and delivery'
              },
              {
                icon: '🏗️',
                title: 'End-to-End Solutions',
                description: 'Comprehensive services from land acquisition to project handover'
              },
              {
                icon: '🌟',
                title: 'Quality Assurance',
                description: 'Partnerships with top architects, engineers, and material suppliers'
              },
              {
                icon: '📍',
                title: 'Local Knowledge',
                description: 'Deep understanding of Tamil Nadu\'s real estate market and regulatory environment'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.8,
                  delay: index * 0.1,
                  ease: "easeOut"
                }}
                viewport={{ once: true }}
                whileHover={{
                  y: -8,
                  boxShadow: 'var(--shadow-2xl)',
                  transition: { duration: 0.3 }
                }}
              >
                <div className="feature-card-header">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3 className="feature-title">{feature.title}</h3>
                </div>
                <div className="feature-card-content">
                  <p className="feature-description">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Testimonials</h2>
            <p className="section-subtitle">
              What our clients say about our services
            </p>
          </motion.div>

          <motion.div
            className="testimonials-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {[
              {
                content: '"Working with Vibha Corporation was an exceptional experience. Their attention to detail, regulatory knowledge, and professional approach made our commercial project seamless from conception to completion." – One of its Client',
                rating: 5,
                image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face'
              },
              {
                content: '"SRJ\'s expertise in land acquisition and development planning helped us secure prime locations across Tamil Nadu. Their network and market knowledge are unparalleled." – From NDR',
                rating: 5,
                image: 'https://images.unsplash.com/photo-1494790108755-2616b612b550?w=100&h=100&fit=crop&crop=face'
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.2,
                  ease: "easeOut"
                }}
                viewport={{ once: true }}
                whileHover={{
                  y: -5,
                  boxShadow: 'var(--shadow-2xl)',
                  transition: { duration: 0.3 }
                }}
              >
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="star">⭐</span>
                  ))}
                </div>

                <blockquote className="testimonial-content">
                  {testimonial.content}
                </blockquote>

                <div className="testimonial-author">
                  <img
                    src={testimonial.image}
                    alt="Client testimonial"
                    className="author-avatar"
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="contact-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Contact Us</h2>
            <p className="section-subtitle">
              Get in touch for your construction and real estate needs
            </p>
          </motion.div>

          <div className="horizontal-contact-row">
            <motion.div
              className="contact-card"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="contact-card-header">
                <div className="contact-icon">🏢</div>
                <h3 className="contact-card-title">Corporate Office</h3>
              </div>
              <div className="contact-card-content">
                <p>Vibha Corporation & Co</p>
                <p>Old No: 50, New No:8 Plot No :12, Niramaya Flats, 3rd floor, Janakiraman Street, West Mambalam, Chennai-600033</p>
              </div>
            </motion.div>

            <motion.div
              className="contact-card"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="contact-card-header">
                <div className="contact-icon">🏠</div>
                <h3 className="contact-card-title">Registered Office</h3>
              </div>
              <div className="contact-card-content">
                <p>Vibha Corporation & Co</p>
                <p>Plot No 9, Door No 10, Vasudhaalayam, Lakshmi Hayagreeva Nagar, 4th Cross Street, Adambakkam, Chennai-600088</p>
              </div>
            </motion.div>

            <motion.div
              className="contact-card"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="contact-card-header">
                <div className="contact-icon">
                  <LocationIcon title="Service locations" />
                </div>
                <h3 className="contact-card-title">Services Available In</h3>
              </div>
              <div className="contact-card-content">
                <p>Chennai | Coimbatore | Coonoor | Kodaikanal | Madurai | Trichy | Pondicherry</p>
              </div>
            </motion.div>

            <motion.div
              className="contact-card"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="contact-card-header">
                <div className="contact-icon">🕐</div>
                <h3 className="contact-card-title">Business Hours</h3>
              </div>
              <div className="contact-card-content">
                <p>Monday - Saturday: 9:00 AM - 6:00 PM</p>
                <p>Sunday: By Appointment</p>
              </div>
            </motion.div>

            <motion.div
              className="contact-card"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              viewport={{ once: true }}
              whileHover={{
                y: -8,
                boxShadow: 'var(--shadow-2xl)',
                transition: { duration: 0.3 }
              }}
            >
              <div className="contact-card-header">
                <div className="contact-icon">📞</div>
                <h3 className="contact-card-title">Connect With Us</h3>
              </div>
              <div className="contact-card-content">
                <p>Ramsharan S - Manager Business Operations and Development - 7358666389</p>
                <p>Dinesh Kumar - Projects Head - 8870432644</p>
                <p>Sundar Rajan Jagannathan - Managing Partner - 9884875641</p>
                <p>Contact Email: ramsharan@vibhacorporation.in | dinesh@vibhacorporation.in | srj@vibhacorporation.in</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;